import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { complaintAPI, caseAPI } from '../utils/api';
import { AlertCircle, Plus, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientComplaints() {
  const [complaints, setComplaints] = useState([]);
  const [complaintNatures, setComplaintNatures] = useState([]);
  const [myCase, setMyCase] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    complaint_type: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [complaintsRes, casesRes, naturesRes] = await Promise.all([
        complaintAPI.getComplaints(),
        caseAPI.getCases(),
        complaintAPI.getNatures()
      ]);
      setComplaints(complaintsRes.data);
      if (casesRes.data.length > 0) {
        setMyCase(casesRes.data[0]);
      }
      setComplaintNatures(naturesRes.data || []);
      // Set default complaint type if available
      if (naturesRes.data && naturesRes.data.length > 0) {
        setFormData(prev => ({ ...prev, complaint_type: naturesRes.data[0].key }));
      }
    } catch (error) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!myCase) {
      toast.error('No active case found');
      return;
    }
    
    try {
      await complaintAPI.createComplaint({
        case_id: myCase.id,
        ...formData
      });
      toast.success('Complaint submitted successfully! Our team will review it soon.');
      setShowForm(false);
      setFormData({
        subject: '',
        description: '',
        complaint_type: complaintNatures.length > 0 ? complaintNatures[0].key : ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit complaint');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { color: 'bg-amber-100 text-amber-800', text: 'Open' },
      in_progress: { color: 'bg-blue-100 text-blue-800', text: 'In Progress' },
      resolved: { color: 'bg-green-100 text-green-800', text: 'Resolved' }
    };
    const badge = badges[status] || badges.open;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="client-complaints-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Complaints & Support</h1>
            <p className="text-slate-600">Submit and track your service complaints</p>
          </div>
          {myCase && (
            <button
              onClick={() => setShowForm(!showForm)}
              data-testid="add-complaint-button"
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              File Complaint
            </button>
          )}
        </div>

        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="complaint-form">
            <h2 className="text-xl font-bold text-slate-900 mb-4">New Complaint</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Complaint Type *</label>
                <select
                  value={formData.complaint_type}
                  onChange={(e) => setFormData({...formData, complaint_type: e.target.value})}
                  data-testid="select-complaint-type"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                >
                  {complaintNatures.map((nature) => (
                    <option key={nature.id} value={nature.key}>
                      {nature.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  data-testid="input-subject"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  placeholder="Brief description of your issue"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="input-description"
                  rows="4"
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  placeholder="Please provide detailed information about your complaint..."
                  required
                />
              </div>
              <div className="flex gap-4">
                <button type="submit" data-testid="submit-complaint-button" className="btn-primary">
                  Submit Complaint
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Your Complaints</h2>
          {complaints.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No complaints filed yet</p>
              <p className="text-sm text-slate-400 mt-2">
                If you're experiencing any issues, feel free to file a complaint
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {complaints.map((complaint) => (
                <div
                  key={complaint.id}
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-all"
                  data-testid={`complaint-${complaint.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-slate-900">{complaint.subject}</h3>
                      <p className="text-sm text-slate-500">
                        {new Date(complaint.created_at).toLocaleDateString()} - {complaint.complaint_type.replace(/_/g, ' ')}
                      </p>
                    </div>
                    {getStatusBadge(complaint.status)}
                  </div>
                  <p className="text-slate-700 mb-3">{complaint.description}</p>
                  {complaint.admin_response && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm font-semibold text-green-900 mb-1">Admin Response:</p>
                      <p className="text-sm text-green-800">{complaint.admin_response}</p>
                      {complaint.resolved_at && (
                        <p className="text-xs text-green-600 mt-2">
                          Resolved on {new Date(complaint.resolved_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
